import { computed, ComputedRef } from 'vue';
import { useWizardStepNavigation } from '@/composables/useWizardStepNavigation';
import { jointsConfiguratorSteps } from '@/modules/ConfiguratorJoints/helpers/JointsConfiguration';

/**
 * Joints-configurator step navigation.
 *
 * Thin wrapper around the generic `useWizardStepNavigation`. Owns the
 * joints-specific step labels, descriptions, completion rule wiring and
 * side-effects (e.g. wiping assembly-position values when leaving step 2
 * backwards).
 *
 * Step 5 (additional options) is conditional: it is disabled and skipped when
 * `includesOptionsStep` is false (Type 1 corner or "brez vodil" guide).
 * The progress bar still renders all steps so it does not jump between five
 * and six visual slots.
 */
export interface UseJointsStepNavigationOptions {
    isPositionSelectionCompleted: ComputedRef<boolean>;
    isPositionOrderCompleted: ComputedRef<boolean>;
    isShapeAndAngleCompleted: ComputedRef<boolean>;
    areGuidesConfigured: ComputedRef<boolean>;
    /** Whether the conditional options step (step 5) is part of the wizard. */
    includesOptionsStep: ComputedRef<boolean>;
    /** Side-effect: wipe assembly-position values when leaving step 2 backwards. */
    clearAssemblyPositionValues: () => void;
    resetShapeAndAngle: () => void;
    resetGuidesAndType: () => void;
    resetOptions: () => void;
}

const POSITION_ORDER_STEP_NUMBER = 2;
const SHAPE_AND_ANGLE_STEP_NUMBER = 3;
const GUIDES_STEP_NUMBER = 4;
const OPTIONS_STEP_NUMBER = 5;
const PREVIEW_STEP_NUMBER = 6;

const OPTIONS_STEP_INDEX = 4;
const MAX_STEP_COUNT = jointsConfiguratorSteps.length;

export function useJointsStepNavigation(options: UseJointsStepNavigationOptions) {
    const {
        isPositionSelectionCompleted,
        isPositionOrderCompleted,
        isShapeAndAngleCompleted,
        areGuidesConfigured,
        includesOptionsStep,
        clearAssemblyPositionValues,
        resetShapeAndAngle,
        resetGuidesAndType,
        resetOptions,
    } = options;

    const totalSteps = computed<number>(() => MAX_STEP_COUNT);

    // The options step is skippable, so it is always "complete".
    const stepCompletions = computed<boolean[]>(() => [
        isPositionSelectionCompleted.value,
        isPositionOrderCompleted.value,
        isShapeAndAngleCompleted.value,
        areGuidesConfigured.value,
        true,
        true,
    ]);

    const navigation = useWizardStepNavigation({
        totalSteps,
        stepCompletions,
        onLeaveStep: (_stepBeingLeft, direction, targetStep) => {
            if (direction !== 'backward') {
                return;
            }

            if (targetStep === POSITION_ORDER_STEP_NUMBER - 1) {
                clearAssemblyPositionValues();
            }

            if (targetStep === POSITION_ORDER_STEP_NUMBER) {
                resetShapeAndAngle();
            }

            if (targetStep === SHAPE_AND_ANGLE_STEP_NUMBER) {
                resetGuidesAndType();
            }

            if (targetStep === GUIDES_STEP_NUMBER) {
                resetOptions();
            }
        },
    });

    const stepsAccessible = computed<boolean[]>(() =>
        navigation.stepsAccessible.value.map((isAccessible, stepIndex) =>
            stepIndex === OPTIONS_STEP_INDEX && !includesOptionsStep.value ? false : isAccessible,
        ),
    );

    const canGoToNextStep = computed<boolean>(() => {
        if (navigation.currentStep.value === GUIDES_STEP_NUMBER && !includesOptionsStep.value) {
            return areGuidesConfigured.value;
        }
        return navigation.canGoToNextStep.value;
    });

    function goToNextStep() {
        if (!canGoToNextStep.value) {
            return;
        }

        if (navigation.currentStep.value === GUIDES_STEP_NUMBER && !includesOptionsStep.value) {
            navigation.goToStep(PREVIEW_STEP_NUMBER);
            return;
        }

        navigation.goToNextStep();
    }

    function goToPrevStep() {
        if (navigation.currentStep.value === PREVIEW_STEP_NUMBER && !includesOptionsStep.value) {
            navigation.goToStep(GUIDES_STEP_NUMBER);
            return;
        }

        navigation.goToPrevStep();
    }

    function goToStep(step: number) {
        if (step === OPTIONS_STEP_NUMBER && !includesOptionsStep.value) {
            return;
        }

        navigation.goToStep(step);
    }

    const currentStepDescription = computed<string>(
        () => jointsConfiguratorSteps[navigation.currentStep.value - 1]?.description ?? '',
    );

    return {
        ...navigation,
        stepLabels: computed<string[]>(() => jointsConfiguratorSteps.map((step) => step.name)),
        currentStepDescription,
        stepsAccessible,
        canGoToNextStep,
        goToNextStep,
        goToPrevStep,
        goToStep,
        totalSteps,
    };
}
